import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Download, Eye, Lock, Unlock, Wallet, PackageMinus } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { api, errMsg, inr, fmtDate, today, openFile } from "@/lib/api";
import { PageHeader, StatCard, Field, TableWrap, Empty, th, td, tr } from "@/components/common";
import { downloadCsv } from "@/components/market";
import { BillDialog, SupplierDialog, PayDialog } from "@/components/purchases";
import { useAuth } from "@/context/AuthContext";

const ST_CLS = { Paid: "text-emerald-600", "Partially Paid": "text-amber-600", Unpaid: "text-rose-600" };

function BillsTab({ meta, suppliers, month, reloadAll }) {
  const [bills, setBills] = useState([]);
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const [pay, setPay] = useState(null);
  const load = () => api.get("/purchases/bills", { params: month ? { month } : {} }).then((r) => { setBills(r.data); setPay((p) => p && r.data.find((b) => b.id === p.id)); }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [month]); // eslint-disable-line react-hooks/exhaustive-deps
  const done = () => { load(); reloadAll(); };
  const del = async (b) => {
    if (!window.confirm(`Delete bill ${b.bill_no}? Stock & payments of this bill are removed too.`)) return;
    try { await api.delete(`/purchases/bills/${b.id}`); toast.success("Bill deleted"); done(); } catch (e) { toast.error(errMsg(e)); }
  };
  const csv = () => downloadCsv(`Purchases_${month || "all"}.csv`, ["Date", "Bill No", "Supplier", "GSTIN", "Category", "Vehicle", "Taxable", "GST %", "CGST", "SGST", "IGST", "Non-GST", "Total", "Paid", "Outstanding", "Status"],
    bills.map((b) => [b.bill_date, b.bill_no, b.supplier_name, b.supplier_gstin, b.category_label, b.vehicle_number || "", b.taxable, b.gst_rate, b.cgst, b.sgst, b.igst, b.non_gst, b.total, b.paid, b.outstanding, b.status]));
  return (
    <div>
      <div className="mb-3 flex flex-wrap justify-end gap-2">
        <Button size="sm" variant="outline" data-testid="bills-csv-btn" disabled={!bills.length} onClick={csv}><Download className="mr-1 h-4 w-4" />Excel / CSV</Button>
        <Button size="sm" data-testid="add-bill-btn" onClick={() => setDlg({ open: true, initial: null })}><Plus className="mr-1 h-4 w-4" />New Bill</Button>
      </div>
      <TableWrap>
        <thead><tr>{["Date", "Bill No", "Supplier", "Category", "Vehicle / Items", "Taxable", "GST", "Non-GST", "Total", "Outstanding", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {bills.map((b) => (
            <tr key={b.id} className={tr} data-testid={`bill-row-${b.bill_no}`}>
              <td className={`${td} whitespace-nowrap`}>{fmtDate(b.bill_date)}{b.locked && <Lock className="ml-1 inline h-3 w-3 text-muted-foreground" />}</td>
              <td className={`${td} font-mono text-xs font-semibold`}>{b.bill_no}</td>
              <td className={td}>{b.supplier_name}<div className="text-[10px] text-muted-foreground">{b.supplier_gstin}</div></td>
              <td className={`${td} text-xs`}>{b.category_label}</td>
              <td className={`${td} text-xs`}>{b.vehicle_number && <span className="font-mono">{b.vehicle_number}</span>}{b.items.length > 0 && <div className="text-muted-foreground">{b.items.map((it) => `${it.qty}× ${it.name} → ${it.placement === "vehicle" ? it.vehicle_number : "Stock"}`).join(", ")}</div>}{!b.vehicle_number && !b.items.length && "-"}</td>
              <td className={`${td} amount`}>{inr(b.taxable, 2)}</td>
              <td className={`${td} amount text-xs`}>{inr(b.gst_amount, 2)}<div className="text-[10px] text-muted-foreground">{b.gst_rate}% {b.intra ? "C+S" : "IGST"}</div></td>
              <td className={`${td} amount`}>{b.non_gst ? inr(b.non_gst, 2) : "-"}</td>
              <td className={`${td} amount font-semibold`}>{inr(b.total, 2)}</td>
              <td className={`${td} amount font-semibold ${ST_CLS[b.status]}`} data-testid={`bill-outstanding-${b.bill_no}`}>{inr(b.outstanding, 2)}<div className="text-[10px] font-normal">{b.status}</div></td>
              <td className={td}>
                <div className="flex justify-end gap-1">
                  {b.file_id && <Button size="icon" variant="ghost" data-testid={`bill-file-${b.bill_no}`} onClick={() => openFile(b.file_id).catch((e) => toast.error(errMsg(e)))}><Eye className="h-4 w-4" /></Button>}
                  <Button size="icon" variant="ghost" title="Payments" data-testid={`bill-pay-${b.bill_no}`} onClick={() => setPay(b)}><Wallet className="h-4 w-4" /></Button>
                  {!b.locked && <>
                    <Button size="icon" variant="ghost" data-testid={`bill-edit-${b.bill_no}`} onClick={() => setDlg({ open: true, initial: b })}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" data-testid={`bill-delete-${b.bill_no}`} onClick={() => del(b)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                  </>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!bills.length && <Empty testid="bills-empty" text="No purchase bills in this period" />}
      <BillDialog open={dlg.open} initial={dlg.initial} meta={meta} suppliers={suppliers} onClose={() => setDlg({ open: false, initial: null })} onSaved={() => { setDlg({ open: false, initial: null }); done(); }} />
      <PayDialog bill={pay} meta={meta} onClose={() => setPay(null)} onSaved={done} />
    </div>
  );
}

function SuppliersTab({ suppliers, reload }) {
  const [dlg, setDlg] = useState({ open: false, initial: null });
  const [st, setSt] = useState(null);
  const del = async (s) => {
    if (!window.confirm(`Delete supplier ${s.name}?`)) return;
    try { await api.delete(`/purchases/suppliers/${s.id}`); toast.success("Supplier deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const openSt = (s) => api.get(`/purchases/suppliers/${s.id}/statement`).then((r) => setSt(r.data)).catch((e) => toast.error(errMsg(e)));
  return (
    <div>
      <div className="mb-3 flex justify-end"><Button size="sm" data-testid="add-supplier-btn" onClick={() => setDlg({ open: true, initial: null })}><Plus className="mr-1 h-4 w-4" />New Supplier</Button></div>
      <TableWrap>
        <thead><tr>{["Supplier", "GSTIN", "State", "Mobile", "Bills", "Billed", "Paid", "Payable", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {suppliers.map((s) => (
            <tr key={s.id} className={tr} data-testid={`supplier-row-${s.id}`}>
              <td className={`${td} font-semibold`}>{s.name}</td>
              <td className={`${td} font-mono text-xs`}>{s.gstin || "-"}</td>
              <td className={td}>{s.state || "-"}</td>
              <td className={td}>{s.phone || "-"}</td>
              <td className={td}>{s.stats.bills}</td>
              <td className={`${td} amount`}>{inr(s.stats.billed)}</td>
              <td className={`${td} amount`}>{inr(s.stats.paid)}</td>
              <td className={`${td} amount font-semibold ${s.stats.outstanding > 0 ? "text-rose-600" : ""}`} data-testid={`supplier-payable-${s.id}`}>{inr(s.stats.outstanding)}</td>
              <td className={td}>
                <div className="flex justify-end gap-1">
                  <Button size="sm" variant="outline" data-testid={`supplier-statement-${s.id}`} onClick={() => openSt(s)}>Ledger</Button>
                  <Button size="icon" variant="ghost" data-testid={`supplier-edit-${s.id}`} onClick={() => setDlg({ open: true, initial: s })}><Pencil className="h-4 w-4" /></Button>
                  <Button size="icon" variant="ghost" data-testid={`supplier-delete-${s.id}`} onClick={() => del(s)}><Trash2 className="h-4 w-4 text-rose-600" /></Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!suppliers.length && <Empty testid="suppliers-empty" text="No suppliers yet" />}
      <SupplierDialog open={dlg.open} initial={dlg.initial} onClose={() => setDlg({ open: false, initial: null })} onSaved={() => { setDlg({ open: false, initial: null }); reload(); }} />
      <Dialog open={!!st} onOpenChange={() => setSt(null)}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto bg-card" data-testid="supplier-ledger-dialog">
          <DialogHeader><DialogTitle>Supplier Ledger — {st?.supplier.name}</DialogTitle></DialogHeader>
          {st && <>
            <TableWrap>
              <thead><tr>{["Date", "Particulars", "Bill (+)", "Paid (−)", "Balance"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
              <tbody>{st.rows.map((r, i) => <tr key={i} className={tr}><td className={`${td} whitespace-nowrap`}>{fmtDate(r.date)}</td><td className={`${td} text-xs`}>{r.particulars}</td><td className={`${td} amount`}>{r.bill ? inr(r.bill, 2) : ""}</td><td className={`${td} amount`}>{r.paid ? inr(r.paid, 2) : ""}</td><td className={`${td} amount`}>{inr(r.balance, 2)}</td></tr>)}</tbody>
            </TableWrap>
            <div className="flex justify-between font-bold"><span>Payable</span><span className="amount" data-testid="supplier-ledger-outstanding">{inr(st.outstanding, 2)}</span></div>
            <DialogFooter><Button variant="outline" data-testid="supplier-ledger-csv" onClick={() => downloadCsv(`Supplier_${st.supplier.name}.csv`, ["Date", "Particulars", "Bill", "Paid", "Balance"], st.rows.map((r) => [r.date, r.particulars, r.bill, r.paid, r.balance]))}><Download className="mr-1 h-4 w-4" />CSV</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StockTab({ meta }) {
  const [rows, setRows] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [issue, setIssue] = useState(null);
  const [f, setF] = useState({});
  const load = () => api.get("/purchases/stock").then((r) => setRows(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {}); }, []);
  const cat = Object.fromEntries(meta.categories.map((c) => [c.key, c.label]));
  const save = async (e) => {
    e.preventDefault();
    try { await api.post(`/purchases/stock/${issue.id}/issue`, { ...f, qty: Number(f.qty) }); toast.success("Issued to vehicle — cost added to vehicle"); setIssue(null); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const reverse = async (m) => {
    if (!window.confirm("Reverse this issue (item back to stock)?")) return;
    try { await api.delete(`/purchases/stock/moves/${m.id}`); toast.success("Reversed"); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div>
      <TableWrap>
        <thead><tr>{["Item", "Category", "In Stock", "Avg Rate (incl. GST)", "Stock Value", "Recent Movement", ""].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((s) => (
            <tr key={s.id} className={tr} data-testid={`stock-row-${s.id}`}>
              <td className={`${td} font-semibold`}>{s.name}</td>
              <td className={`${td} text-xs`}>{cat[s.category]}</td>
              <td className={`${td} font-bold ${s.qty <= 0 ? "text-rose-600" : ""}`} data-testid={`stock-qty-${s.id}`}>{s.qty}</td>
              <td className={`${td} amount`}>{inr(s.avg_rate, 2)}</td>
              <td className={`${td} amount`}>{inr(s.qty * s.avg_rate)}</td>
              <td className={`${td} text-xs`}>{s.moves.slice(0, 3).map((m) => (
                <div key={m.id} className="flex items-center gap-1">{fmtDate(m.date)} · {m.qty > 0 ? `+${m.qty} (Bill ${m.bill_no})` : `${m.qty} → ${m.vehicle_number}`}
                  {m.kind === "issue" && <button className="text-rose-600" data-testid={`stock-reverse-${m.id}`} onClick={() => reverse(m)}><Trash2 className="h-3 w-3" /></button>}</div>
              ))}</td>
              <td className={td}><Button size="sm" variant="outline" disabled={s.qty <= 0} data-testid={`stock-issue-${s.id}`} onClick={() => { setIssue(s); setF({ qty: 1, vehicle_id: "", date: today(), note: "" }); }}><PackageMinus className="mr-1 h-3.5 w-3.5" />Gaadi me lagao</Button></td>
            </tr>
          ))}
        </tbody>
      </TableWrap>
      {!rows.length && <Empty testid="stock-empty" text="Stock is empty — items marked 'Stock me' on bills appear here" />}
      <Dialog open={!!issue} onOpenChange={() => setIssue(null)}>
        <DialogContent className="max-w-md bg-card" data-testid="stock-issue-dialog">
          <DialogHeader><DialogTitle>Issue {issue?.name} (in stock: {issue?.qty})</DialogTitle></DialogHeader>
          <form onSubmit={save} className="grid grid-cols-2 gap-3">
            <Field label="Qty *"><Input type="number" min="0.01" step="0.01" required data-testid="issue-qty" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} /></Field>
            <Field label="Date"><Input type="date" data-testid="issue-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Vehicle *" className="col-span-2"><select required data-testid="issue-vehicle" className="native-select" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })}><option value="">Select…</option>{vehicles.map((v) => <option key={v.id} value={v.id}>{v.vehicle_number}</option>)}</select></Field>
            <Field label="Note" className="col-span-2"><Input data-testid="issue-note" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} /></Field>
            <DialogFooter className="col-span-2"><Button type="submit" data-testid="issue-save-btn">Issue</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ItcTab({ month }) {
  const [d, setD] = useState(null);
  const m = month || today().slice(0, 7);
  useEffect(() => { api.get("/purchases/itc", { params: { month: m } }).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, [m]);
  if (!d) return null;
  const t = d.totals;
  const csv = () => downloadCsv(`ITC_${m}.csv`, ["Date", "Bill No", "Supplier", "GSTIN", "State", "Category", "Taxable", "GST %", "CGST", "SGST", "IGST", "Total GST", "Non-GST", "Bill Total"],
    [...d.rows.map((r) => [r.bill_date, r.bill_no, r.supplier_name, r.supplier_gstin, r.supplier_state, r.category_label, r.taxable, r.gst_rate, r.cgst, r.sgst, r.igst, r.gst_amount, r.non_gst, r.total]),
      ["TOTAL", "", "", "", "", "", t.taxable, "", t.cgst, t.sgst, t.igst, t.gst_amount, t.non_gst, t.total]]);
  return (
    <div>
      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-5">
        <StatCard label="Taxable Purchases" value={inr(t.taxable)} testid="itc-taxable" />
        <StatCard label="CGST + SGST" value={inr(t.cgst + t.sgst)} testid="itc-cs" />
        <StatCard label="IGST" value={inr(t.igst)} testid="itc-igst" />
        <StatCard label="Eligible ITC (with GSTIN)" value={inr(d.eligible_itc)} accent="text-emerald-600" testid="itc-eligible" />
        <StatCard label="Non-GST" value={inr(t.non_gst)} testid="itc-nongst" />
      </div>
      <div className="mb-3 flex items-center justify-between">
        <div className="text-xs text-muted-foreground">{m} {d.locked && <span className="ml-1 font-semibold text-amber-600"><Lock className="inline h-3 w-3" /> Locked</span>} · Rate-wise: {d.by_rate.map((r) => `${r.gst_rate}% → ${inr(r.gst_amount)}`).join(" · ") || "-"}</div>
        <Button size="sm" variant="outline" data-testid="itc-csv-btn" disabled={!d.rows.length} onClick={csv}><Download className="mr-1 h-4 w-4" />GSTR-2B CSV</Button>
      </div>
      <TableWrap>
        <thead><tr>{["Date", "Bill No", "Supplier", "GSTIN", "Taxable", "Rate", "CGST", "SGST", "IGST", "Non-GST", "Total"].map((h) => <th key={h} className={th}>{h}</th>)}</tr></thead>
        <tbody>{d.rows.map((r) => (
          <tr key={r.id} className={tr} data-testid={`itc-row-${r.bill_no}`}>
            <td className={td}>{fmtDate(r.bill_date)}</td><td className={`${td} font-mono text-xs`}>{r.bill_no}</td><td className={td}>{r.supplier_name}</td>
            <td className={`${td} font-mono text-xs ${r.supplier_gstin ? "" : "text-amber-600"}`}>{r.supplier_gstin || "No GSTIN"}</td>
            {["taxable"].map((k) => <td key={k} className={`${td} amount`}>{inr(r[k], 2)}</td>)}<td className={td}>{r.gst_rate}%</td>
            {["cgst", "sgst", "igst", "non_gst", "total"].map((k) => <td key={k} className={`${td} amount`}>{inr(r[k], 2)}</td>)}
          </tr>
        ))}</tbody>
      </TableWrap>
      {!d.rows.length && <Empty testid="itc-empty" text="No bills this month" />}
    </div>
  );
}

function LockTab() {
  const { role } = useAuth();
  const [locks, setLocks] = useState([]);
  const [m, setM] = useState(today().slice(0, 7));
  const load = () => api.get("/purchases/locks").then((r) => setLocks(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = async (month, locked) => {
    try { await api.post("/purchases/locks", { month, locked }); toast.success(locked ? `${month} locked` : `${month} unlocked`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-lg space-y-4">
      <p className="text-sm text-muted-foreground">Locked months cannot have purchase bills added, edited or deleted. Payments can still be recorded.</p>
      {role === "admin" ? (
        <div className="flex gap-2"><Input type="month" className="w-44" data-testid="lock-month-input" value={m} onChange={(e) => setM(e.target.value)} />
          <Button data-testid="lock-month-btn" onClick={() => set(m, true)}><Lock className="mr-1 h-4 w-4" />Lock Month</Button></div>
      ) : <p className="text-xs text-amber-600">Only admin can lock / unlock months.</p>}
      <div className="space-y-2">
        {locks.map((x) => (
          <div key={x} className="flex items-center justify-between rounded-lg border p-3" data-testid={`lock-row-${x}`}>
            <span className="flex items-center gap-2 font-semibold"><Lock className="h-4 w-4 text-amber-600" />{x}</span>
            {role === "admin" && <Button size="sm" variant="outline" data-testid={`unlock-${x}`} onClick={() => set(x, false)}><Unlock className="mr-1 h-3.5 w-3.5" />Unlock</Button>}
          </div>
        ))}
        {!locks.length && <p className="text-sm text-muted-foreground">No locked months.</p>}
      </div>
    </div>
  );
}

export default function Purchases({ initialTab = "bills" }) {
  const [meta, setMeta] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [month, setMonth] = useState(today().slice(0, 7));
  const loadSup = () => api.get("/purchases/suppliers").then((r) => setSuppliers(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { api.get("/purchases/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(errMsg(e))); loadSup(); }, []);
  if (!meta) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const payable = suppliers.reduce((s, x) => s + x.stats.outstanding, 0);
  return (
    <div>
      <PageHeader title="Purchases & Bills" subtitle={`Supplier bills, GST input credit, stock & payables · Company state: ${meta.company_state || "not set"}`}>
        <Input type="month" className="w-44" data-testid="pur-month-filter" value={month} onChange={(e) => setMonth(e.target.value)} />
        <Button variant="outline" data-testid="pur-all-time-btn" onClick={() => setMonth("")}>All time</Button>
      </PageHeader>
      <div className="mb-5 grid grid-cols-2 gap-4 lg:grid-cols-3">
        <StatCard label="Suppliers" value={suppliers.length} testid="pur-supplier-count" />
        <StatCard label="Total Supplier Payable" value={inr(payable)} accent="text-rose-600" testid="pur-total-payable" />
      </div>
      <Tabs key={initialTab} defaultValue={initialTab}>
        <TabsList className="flex h-auto flex-wrap">
          <TabsTrigger value="bills" data-testid="pur-tab-bills">Bills</TabsTrigger>
          <TabsTrigger value="suppliers" data-testid="pur-tab-suppliers">Suppliers & Ledger</TabsTrigger>
          <TabsTrigger value="stock" data-testid="pur-tab-stock">Inventory Stock</TabsTrigger>
          <TabsTrigger value="itc" data-testid="pur-tab-itc">ITC Report</TabsTrigger>
          <TabsTrigger value="lock" data-testid="pur-tab-lock">Month Lock</TabsTrigger>
        </TabsList>
        <TabsContent value="bills"><BillsTab meta={meta} suppliers={suppliers} month={month} reloadAll={loadSup} /></TabsContent>
        <TabsContent value="suppliers"><SuppliersTab suppliers={suppliers} reload={loadSup} /></TabsContent>
        <TabsContent value="stock"><StockTab meta={meta} /></TabsContent>
        <TabsContent value="itc"><ItcTab month={month} /></TabsContent>
        <TabsContent value="lock"><LockTab /></TabsContent>
      </Tabs>
    </div>
  );
}
